import { useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";
import { ShieldCheck, Check, AlertCircle, Eye, EyeOff } from "lucide-react";

/**
 * Last-resort admin bootstrap page.
 * Requires:
 *   - BOOTSTRAP_TOKEN env var set in production
 *   - Backend redeployed with the /api/auth/bootstrap-admin endpoint
 *
 * Visit /bootstrap, paste the token, set the admin email + password, click Save.
 * After success: delete the BOOTSTRAP_TOKEN env var and redeploy to disable.
 */
export default function Bootstrap() {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("Admin");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/auth/bootstrap-admin`,
        { email: email.trim().toLowerCase(), password, name: name.trim() },
        { headers: { "X-Bootstrap-Token": token.trim(), "Content-Type": "application/json" } }
      );
      setResult(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 503) setError("Bootstrap not configured. Set BOOTSTRAP_TOKEN env var (32+ chars) in production, redeploy, then try again.");
      else if (status === 401) setError("Wrong bootstrap token. Double-check the value in your Custom Keys.");
      else if (status === 400) setError(detail || "Email and password (8+ chars) required.");
      else setError(detail || "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center px-4 py-12" data-testid="bootstrap-page">
      <div className="w-full max-w-md">
        <div className="flex items-baseline gap-2 mb-6">
          <span className="text-orange-600 text-[10px] tracking-[0.3em] uppercase font-bold">Rodeo</span>
          <span className="font-bold uppercase tracking-tight text-2xl leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Cut Studio</span>
        </div>

        <div className="border border-stone-800 p-6">
          <div className="inline-flex items-center gap-2 text-orange-500 text-xs uppercase tracking-widest mb-3">
            <ShieldCheck size={14} /> Emergency Admin Setup
          </div>
          <h1 className="font-display uppercase text-3xl font-bold leading-[0.95] mb-2">Reset Admin</h1>
          <p className="text-stone-400 text-sm mb-6">
            Use this once to create or reset the admin login when you've been locked out. After it works, delete <code className="text-orange-400">BOOTSTRAP_TOKEN</code> from your Custom Keys and redeploy.
          </p>

          {result ? (
            <div className="border border-emerald-700 bg-emerald-500/10 p-4 text-sm" data-testid="bootstrap-success">
              <div className="inline-flex items-center gap-2 text-emerald-400 font-semibold mb-2">
                <Check size={14} /> Admin {result.action === "created" ? "created" : "reset"}
              </div>
              <div className="text-stone-300">You can now sign in:</div>
              <div className="font-mono-tc text-xs mt-2">
                <div><span className="text-stone-500">email:</span> {result.email}</div>
                <div><span className="text-stone-500">password:</span> the one you just set</div>
              </div>
              <a href="/login" className="btn-primary inline-block mt-4 text-xs" data-testid="bootstrap-go-login">Go to login →</a>
              <div className="text-amber-400/80 text-[11px] mt-4">
                ⚠ Now delete <code>BOOTSTRAP_TOKEN</code> from your Custom Keys and redeploy to disable this page.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="bootstrap-form">
              <Field label="Bootstrap token" hint="Set in production Custom Keys → BOOTSTRAP_TOKEN">
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="field-input w-full font-mono-tc text-xs"
                  placeholder="paste your token"
                  required
                  autoComplete="off"
                  data-testid="bootstrap-token"
                />
              </Field>
              <Field label="Admin email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field-input w-full"
                  placeholder="rodeoadmin@rodeocutstudio.com"
                  required
                  data-testid="bootstrap-email"
                />
              </Field>
              <Field label="Admin name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="field-input w-full"
                  data-testid="bootstrap-name"
                />
              </Field>
              <Field label="New password" hint="Min 8 characters">
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="field-input w-full pr-9"
                    minLength={8}
                    required
                    data-testid="bootstrap-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-200"
                    title={show ? "Hide" : "Show"}
                  >
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </Field>

              {error && (
                <div className="border border-rose-700 bg-rose-500/10 p-3 text-xs text-rose-300 inline-flex items-start gap-2" data-testid="bootstrap-error">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="bootstrap-submit"
              >
                <ShieldCheck size={13} /> {busy ? "Saving…" : "Reset Admin Login"}
              </button>
            </form>
          )}
        </div>

        <div className="mt-4 text-[11px] text-stone-600 text-center">
          Lock this page after use by clearing the token env var.
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[11px] text-stone-500 uppercase tracking-widest">{label}</div>
        {hint && <div className="text-[10px] text-stone-600">{hint}</div>}
      </div>
      {children}
    </div>
  );
}
